import i18n from '@dms/i18n';

export type NewVehicleDeliveryFlag = {label: string; colorScheme: 'red' | 'blue'};

/** The arrival facts a sale vehicle carries; anything with them can ask for the flag. */
export type NewVehicleDeliveryFacts = {
  incoming?: boolean;
  hasUnarrivedVehicleOrder?: boolean;
};

/**
 * A stock type on a car that has not arrived is a lie: the order may still be in transit, or
 * cancelled and never coming at all. Wherever a vehicle's sale type is flagged, this replaces it
 * until an arrival record exists, so "In stock" never appears for a car nobody can hand over.
 *
 * Mirrors the backend's own `deliveryState` derivation (D53), including its fallback for records
 * predating the independent unarrived fact. Returns null once the car has arrived, when it never
 * had a new-vehicle order, and for a backend that does not send the facts yet.
 */
export const getNewVehicleDeliveryFlag = (
  saleVehicle: NewVehicleDeliveryFacts | undefined
): NewVehicleDeliveryFlag | null => {
  const isUnarrived =
    saleVehicle?.hasUnarrivedVehicleOrder === true ||
    (saleVehicle?.hasUnarrivedVehicleOrder === undefined && saleVehicle?.incoming === true);

  if (!isUnarrived) {
    return null;
  }

  return saleVehicle?.incoming === true
    ? {
        label: i18n.t('entity.newVehicle.labels.incomingFlag', {defaultValue: 'Incoming'}),
        colorScheme: 'blue',
      }
    : {
        label: i18n.t('entity.newVehicle.labels.orderCancelledFlag', {
          defaultValue: 'Order cancelled',
        }),
        colorScheme: 'red',
      };
};

/** Cancelled is the unarrived state that is no longer expected; the caller usually warns on it. */
export const isNewVehicleOrderCancelled = (
  saleVehicle: NewVehicleDeliveryFacts | undefined
): boolean => saleVehicle?.hasUnarrivedVehicleOrder === true && saleVehicle?.incoming !== true;
